import { ref, Ref } from 'vue';
import api from '@/services/api';

interface ProductCardType {
  id: string;
  name: string;
  slug: string;
  price: string;
  mainPicture: string | null;
  followersCount: number;
  city: string;
  username: string;
}

interface Meta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface Filters {
  search?: string;
  categoryId?: string;
  minPrice?: number;
  maxPrice?: number;
}

interface ProfileProductsResponse {
  status: string;
  message: string;
  data: {
    products: any[];
    meta: Meta;
  };
}

export function useProfileProducts(userId: Ref<string>) {
  const products = ref<ProductCardType[]>([]);
  const meta = ref<Meta | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const currentPage = ref(1);
  const activeFilters = ref<Filters>({});

  const startLoader = () => {
    loading.value = true;
  };

  const stopLoader = () => {
    loading.value = false;
  };

  const fetchProducts = async (page: number = 1, filters: Filters = {}) => {
    if (!userId.value) return;

    startLoader();
    error.value = null;

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());

      // Add active filters to parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value.toString());
        }
      });

      const queryString = params.toString();
      const url = `/api/products/user/${userId.value}/detailed${
        queryString ? '?' + queryString : ''
      }`;

      const response = await api.get<ProfileProductsResponse>(url);

      if (response.data.status === 'success') {
        currentPage.value = page;
        meta.value = response.data.data.meta;

        // Get user info from first product or use empty values
        const userInfo = {
          city: '',
          username: '',
        };

        // Transform products to match what ProductCard expects
        products.value = response.data.data.products.map((p) => ({
          id: p.id,
          name: p.name,
          slug: p.slug || p.id,
          price: p.price,
          mainPicture: p.mainPicture,
          followersCount: p.followersCount || 0,
          city: userInfo.city,
          username: userInfo.username,
        }));
      } else {
        error.value = 'Greška prilikom učitavanja proizvoda.';
      }
    } catch (err) {
      console.error('Error fetching user products:', err);
      error.value = 'Greška prilikom komunikacije sa serverom.';
    } finally {
      stopLoader();
    }
  };

  const changePage = (page: number) => {
    if (
      page >= 1 &&
      meta.value &&
      page <= meta.value.totalPages &&
      page !== currentPage.value
    ) {
      fetchProducts(page, activeFilters.value);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleFilterChange = (filters: Filters) => {
    activeFilters.value = { ...filters };
    currentPage.value = 1; // Reset to first page when filters change
    fetchProducts(1, activeFilters.value);
  };

  const removeFilter = (key: string) => {
    const newFilters = { ...activeFilters.value } as Record<string, any>;
    delete newFilters[key];
    activeFilters.value = newFilters;
    fetchProducts(1, activeFilters.value);
  };

  const resetFilters = () => {
    activeFilters.value = {};
    fetchProducts(1, {});
  };

  const paginationRange = (
    currentPage: number,
    totalPages: number
  ): (number | string)[] => {
    if (!totalPages) return [];

    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let range: (number | string)[] = [];
    range.push(1);

    if (currentPage > 3) {
      range.push('...');
    }

    const startPage = Math.max(2, currentPage - 1);
    const endPage = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startPage; i <= endPage; i++) {
      range.push(i);
    }

    if (currentPage < totalPages - 2) {
      range.push('...');
    }

    if (totalPages > 1) {
      range.push(totalPages);
    }

    return range;
  };

  return {
    products,
    meta,
    loading,
    error,
    currentPage,
    activeFilters,
    fetchProducts,
    changePage,
    handleFilterChange,
    removeFilter,
    resetFilters,
    paginationRange,
  };
}
